<script setup lang="ts">
import { computed } from 'vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import Rating from '../components/Rating.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useNpsStore } from '../stores/npsStore';
import type { NetPromoterScore } from '../models/NetPromoterScore';

const router = useRouter();
const commissioningDeviceStore = useCommissioningDeviceStore();
const npsStore = useNpsStore();

const score = computed<number | undefined>({
  get: () => npsStore.score,
  set: (value) => {
    npsStore.score = value;
  },
});

const comment = computed<string | undefined>({
  get: () => npsStore.comment,
  set: (value) => {
    npsStore.comment = value;
  },
});

const isPersisted = computed(() => {
  return npsStore.persisted;
});

const isRatingRequired = computed(() => {
  return !score.value && comment.value?.trim() !== '';
});

const submitHandler = async () => {
  if (isPersisted.value) {
    navigateNext();
    return; // Already submitted before
  }

  if (isRatingRequired.value) {
    message.error('Rating is required');
    return;
  }

  try {
    await npsStore.submitNetPromoterScore(
      commissioningDeviceStore.serialNumber,
      {
        score: score.value,
        comment: comment.value,
      } as NetPromoterScore,
      router.currentRoute.value.path,
    );
    navigateNext();
  } catch (error) {
    message.error('Error submitting the net promoter score. Please try again.');
  }
};

const navigateNext = () => {
  router.push({ name: 'PostCommissionChecks' });
};
</script>

<template>
  <ProgressFrame pageClass="section-page">
    <template #title>How'd was that?</template>
    <template #subTitle>
      <h4 class="label">
        On a scale of 1-10 how likely is it that you would recommend the SolShare?
      </h4>
    </template>
    <div class="content">
      <Rating
        v-model="score"
        :min="1"
        :max="10"
        :disabled="isPersisted"
        data-tn="nps-score"
      >
        <template #error v-if="isRatingRequired">
          <span>Rating is required</span>
        </template>
      </Rating>
      <div class="feedback-textarea">
        <label class="label" for="feedback">What was the main reason for your score?</label>
        <a-textarea
          v-model:value="comment"
          placeholder="Please provide feedback."
          :rows="6"
          :disabled="isPersisted"
          data-tn="nps-feedback"
        />
      </div>
      <p class="feedback-note">
        This feedback is important for us to improve and provide you better service in the future.
      </p>
      <div class="action-container">
        <ButtonPrimary block @click="submitHandler" data-tn="submit-btn">
          {{ isPersisted ? 'Next' : 'Submit' }}
          <template #iconRight>
            <ChevronRightSVG class="chevron-right-icon" />
          </template>
        </ButtonPrimary>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
}

.label {
  font-size: 1.2rem;
  color: @color-primary;
  font-weight: @font-weight-medium;
}

.feedback-textarea {
  width: 100%;
  margin-top: 0.8rem;

  :deep(textarea) {
    margin-top: 0.8rem;
    padding-top: 0.8rem;
  }
  :deep(textarea::placeholder) {
    font-style: italic;
    color: @color-mid-green;
  }
}

.feedback-note {
  color: @color-mid-green;
  font-style: italic;
}

.action-container {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
}
</style>